import { For } from "solid-js";
import { Icons } from "./Icons";
import { t } from "../lib/i18n";

export type TabId = "profile" | "slots" | "saved";

interface TabBarProps {
  active: TabId;
  onChange: (id: TabId) => void;
}

export function TabBar(props: TabBarProps) {
  const tabs = (): { id: TabId; label: string; Icon: any }[] => [
    { id: "profile", label: t("tabs.profile"), Icon: Icons.Profile },
    { id: "slots", label: t("tabs.slots"), Icon: Icons.SlotIcon },
    { id: "saved", label: t("tabs.saved"), Icon: Icons.Bookmark },
  ];

  return (
    <div
      // Flow-positioned bottom strip. The parent phone-card is flex-col;
      // this is flex-shrink-0 so it stays glued to the bottom no matter how
      // the inner scroll area shifts.
      class="flex-shrink-0 border-t"
      style={{
        "padding-bottom": "calc(var(--app-safe-bottom) + 8px)",
        "padding-top": "6px",
        // Solid tint instead of `backdrop-filter: blur(20px)`, that was a
        // major paint cost on cheap Android (>15ms per scroll frame).
        background: "rgba(255,255,255,0.96)",
        "border-color": "rgba(0,0,0,0.05)",
      }}
      role="tablist"
      aria-label="Primary navigation"
    >
      <div class="relative flex">
        <For each={tabs()}>
          {(tab) => {
            const isActive = () => tab.id === props.active;
            const color = () => (isActive() ? "#b8923f" : "#999");
            return (
              <button
                role="tab"
                aria-selected={isActive()}
                aria-controls={`tab-panel-${tab.id}`}
                onClick={() => props.onChange(tab.id)}
                class="relative flex flex-1 cursor-pointer flex-col items-center gap-1 bg-transparent border-0 px-1"
                style={{
                  // 48 px minimum height puts the active hit-area into iOS's
                  // 44 pt range once you add the safe-area padding above.
                  "min-height": "48px",
                  "padding-top": "8px",
                  "padding-bottom": "4px",
                }}
              >
                {isActive() && (
                  <div
                    class="absolute"
                    style={{
                      top: "-6px",
                      width: "32px",
                      height: "3px",
                      "border-radius": "100px",
                      background: "#b8923f",
                    }}
                  />
                )}
                <tab.Icon size={24} color={color()} filled={isActive() && tab.id !== "slots"} />
                <span
                  style={{
                    // Bumped from 10.5 px → 11.5 px for legibility on tiny
                    // viewports (QA flagged sub-12 px UI text).
                    "font-size": "11.5px",
                    "font-weight": isActive() ? 600 : 500,
                    color: color(),
                    "letter-spacing": "0.1px",
                  }}
                >
                  {tab.label}
                </span>
              </button>
            );
          }}
        </For>
      </div>
    </div>
  );
}
